import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/api";

const LINKS = [
  { to: "/find-care", label: "Find Care" },
  { to: "/jobs", label: "Find Jobs" },
  { to: "/providers", label: "Providers" },
  { to: "/#verified", label: "Zunvel Verified™" },
  { to: "/#for-providers", label: "For Providers" },
];

export default function SiteHeader() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const out = async () => { await logout(); nav("/"); };
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#003B2D]/95 border-b border-[#D4AF63]/25" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <Logo light />
        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive && !l.to.includes("#") ? "text-[#E6C77B]" : "text-[#FAF8F2]/85 hover:text-[#E6C77B]"}`}>{l.label}</NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <NotificationBell dark />
              <Button asChild variant="ghost" className="rounded-full text-[#FAF8F2] hover:bg-white/10 hover:text-[#E6C77B]" data-testid="header-dashboard-btn"><Link to={homeFor(user)}><LayoutDashboard className="h-4 w-4 mr-1.5" />My Zunvel</Link></Button>
              <Button variant="outline" className="rounded-full bg-transparent text-[#FAF8F2] border-[#D4AF63]/50 hover:bg-white/10 hover:text-[#E6C77B]" onClick={out} data-testid="header-logout-btn"><LogOut className="h-4 w-4 mr-1.5" />Sign out</Button>
            </>
          ) : (
            <>
              <Button asChild variant="outline" className="rounded-lg bg-transparent text-[#FAF8F2] border-[#FAF8F2]/40 hover:bg-white/10 hover:text-[#E6C77B]" data-testid="header-login-btn"><Link to="/login">Sign in</Link></Button>
              <Button asChild className="rounded-lg zv-btn-gold font-semibold" data-testid="header-register-btn"><Link to="/register">Create Account</Link></Button>
            </>
          )}
        </div>
        <div className="lg:hidden flex items-center gap-1">
          {user && <span data-testid="mobile-notification-bell"><NotificationBell dark /></span>}
          <button className="p-3 rounded-lg text-[#FAF8F2] hover:bg-white/10" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <nav className="lg:hidden border-t border-[#D4AF63]/20 grid grid-cols-3" data-testid="mobile-quick-nav">
        {[["/find-care", "Find Care"], ["/jobs", "Find Jobs"], ["/#for-providers", "For Providers"]].map(([to, t], i) => (
          <Link key={to} to={to} className={`py-3 text-center text-[13px] font-bold text-[#E6C77B] hover:bg-white/5 ${i ? "border-l border-[#D4AF63]/20" : ""}`} data-testid={`quick-nav-${t.toLowerCase().replace(/ /g, "-")}`}>{t}</Link>
        ))}
      </nav>
      {open && (
        <div className="lg:hidden border-t border-[#D4AF63]/20 bg-[#00281F] px-4 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-4 py-3.5 rounded-xl text-base text-[#FAF8F2] hover:bg-white/10 font-semibold">{l.label}</Link>)}
          <div className="pt-3 flex gap-2">
            {user ? (
              <><Button asChild className="flex-1 h-12 rounded-lg zv-btn-gold font-bold"><Link to={homeFor(user)} onClick={() => setOpen(false)}>My Zunvel</Link></Button>
                <Button variant="outline" className="h-12 rounded-lg bg-transparent text-[#FAF8F2] border-[#FAF8F2]/40" onClick={out}>Sign out</Button></>
            ) : (
              <><Button asChild variant="outline" className="flex-1 h-12 rounded-lg bg-transparent text-[#FAF8F2] border-[#FAF8F2]/40"><Link to="/login" onClick={() => setOpen(false)}>Sign in</Link></Button>
                <Button asChild className="flex-1 h-12 rounded-lg zv-btn-gold font-bold"><Link to="/register" onClick={() => setOpen(false)}>Create Account</Link></Button></>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
